import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../../../utils/apiClient';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Card } from '../../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Badge } from '../../../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Upload, FileText, Trash2, Edit2, Mic, Video, Clock, Pen, Eye, Loader2, AlertCircle } from 'lucide-react';
import EmptyState from '../../../components/EmptyState';

export default function StyleTraining() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [textName, setTextName] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [docName, setDocName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [savedTexts, setSavedTexts] = useState([]);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [itemToRename, setItemToRename] = useState(null);
  const [newName, setNewName] = useState('');
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewContent, setViewContent] = useState(null);
  const [loadingContent, setLoadingContent] = useState(false);

  useEffect(() => {
    fetchSavedTexts();
  }, []);

  const fetchSavedTexts = async () => {
    try {
      const response = await apiClient.get('/vault/items?type=training_text');
      // Filter for Style intent only
      const styleItems = response.data.filter(item => item.intent === 'Style');
      setSavedTexts(styleItems);
    } catch (error) {
      console.error('Failed to fetch style training:', error);
      toast.error(t('errors.loadFailed'));
    }
  };

  const handleTextSubmit = async () => {
    if (!text.trim()) {
      toast.error(t('errors.uploadFailed'));
      return;
    }

    try {
      const blob = new Blob([text], { type: 'text/plain' });
      const file = new File([blob], `style-text-${Date.now()}.txt`, { type: 'text/plain' });

      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', textName || `Style Sample - ${new Date().toLocaleDateString()}`);
      formData.append('description', `Category: Style | ${text.substring(0, 100)}...`);
      formData.append('type', 'training_text');
      formData.append('tags', 'training,style,text');
      formData.append('allowForAI', 'true');
      formData.append('intent', 'Style');

      await apiClient.post('/vault/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success(t('success.saved'));
      setText('');
      setTextName('');
      fetchSavedTexts();
    } catch (error) {
      toast.error(t('errors.saveFailed'));
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('title', docName || selectedFile.name);
      formData.append('description', 'Category: Style | Writing style document');
      formData.append('type', 'training_text');
      formData.append('tags', 'training,style,document');
      formData.append('allowForAI', 'true');
      formData.append('intent', 'Style');

      await apiClient.post('/vault/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success(t('success.uploaded'));
      setSelectedFile(null);
      setDocName('');
      fetchSavedTexts();
    } catch (error) {
      toast.error(t('errors.uploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  const openRenameDialog = (item) => {
    setItemToRename(item);
    setNewName(item.title);
    setRenameDialogOpen(true);
  };

  const openViewDialog = async (item) => {
    setLoadingContent(true);
    setViewDialogOpen(true);
    try {
      const response = await apiClient.get(`/vault/content/${item.fileId}`);
      setViewContent(response.data);
    } catch (error) {
      console.error('Failed to fetch content:', error);
      toast.error('Failed to load content');
      setViewContent({ content: '', title: item.title, previewStatus: 'failed', previewType: 'unknown' });
    } finally {
      setLoadingContent(false);
    }
  };

  // Helper function to get preview type label for style samples
  const getPreviewLabel = (previewType, previewStatus) => {
    if (previewStatus === 'failed' || previewStatus === 'unsupported') {
      return { label: 'Preview Not Available', color: 'bg-gray-100 text-gray-600' };
    }
    
    switch (previewType) {
      case 'docx':
        return { label: 'DOCX · Extracted Text Preview', color: 'bg-purple-50 text-purple-700' };
      case 'pdf':
        if (previewStatus === 'partial') {
          return { label: 'PDF · Partial Text Preview', color: 'bg-amber-50 text-amber-700' };
        }
        return { label: 'PDF · Text Preview', color: 'bg-purple-50 text-purple-700' };
      case 'txt':
        return { label: 'TXT · Original Content', color: 'bg-green-50 text-green-700' };
      case 'pasted':
        return { label: 'Text Entry · Original Content', color: 'bg-green-50 text-green-700' };
      default:
        return { label: 'Text · Original Content', color: 'bg-gray-50 text-gray-600' };
    }
  };

  const handleRename = async () => {
    try {
      await apiClient.put(`/vault/items/${itemToRename.id}?title=${encodeURIComponent(newName)}`);
      toast.success(t('success.updated'));
      setRenameDialogOpen(false);
      fetchSavedTexts();
    } catch (error) {
      toast.error(t('errors.saveFailed'));
    }
  };

  const handleDelete = async (itemId) => {
    if (!confirm(t('mySkills.confirmRemove'))) return;

    try {
      await apiClient.delete(`/vault/items/${itemId}`);
      toast.success(t('success.deleted'));
      fetchSavedTexts();
    } catch (error) {
      toast.error(t('errors.deleteFailed'));
    }
  };

  const getCategory = (item) => {
    return item.description?.includes('Category: Style') ? 'Style' : 'Text';
  };

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/personal/twin/train')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('common.back')}
        </Button>
        <div>
          <h1 className="font-heading text-3xl font-bold text-gray-900">{t('training.styleTraining')}</h1>
          <p className="text-gray-600 mt-1">{t('training.howYouCommunicate')}</p>
        </div>
      </div>

      <Tabs defaultValue="text" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="text">
            <FileText className="w-4 h-4 mr-2" />
            {t('training.style')}
          </TabsTrigger>
          <TabsTrigger value="voice" disabled>
            <Mic className="w-4 h-4 mr-2" />
            Voice
            <Badge className="ml-2 text-xs bg-gray-200 text-gray-600">Soon</Badge>
          </TabsTrigger>
          <TabsTrigger value="video" disabled>
            <Video className="w-4 h-4 mr-2" />
            Video
            <Badge className="ml-2 text-xs bg-gray-200 text-gray-600">Soon</Badge>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="text" className="space-y-6 mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="p-6 rounded-2xl space-y-4">
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                <FileText className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="font-heading text-xl font-semibold">{t('training.addTextSample')}</h3>
              <p className="text-sm text-gray-600">
                {t('emptyState.tipStyleTraining')}
              </p>

              <div>
                <Label>{t('training.title_label')}</Label>
                <Input
                  value={textName}
                  onChange={(e) => setTextName(e.target.value)}
                  placeholder="e.g., Email to colleague, Journal Entry"
                  className="mt-1"
                />
              </div>

              <Textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={t('training.pasteTextPlaceholder')}
                rows={8}
                className="resize-none"
              />
              
              <Button onClick={handleTextSubmit} className="w-full bg-purple-600 hover:bg-purple-700" disabled={!text.trim()}>
                {t('common.save')}
              </Button>
            </Card>

            <Card className="p-6 rounded-2xl space-y-4">
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                <Upload className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="font-heading text-xl font-semibold">{t('training.uploadDocument')}</h3>
              <p className="text-sm text-gray-600">
                Upload TXT, PDF, or DOCX files
              </p>

              <label htmlFor="doc-upload">
                <div className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all ${
                  selectedFile 
                    ? 'border-purple-600 bg-purple-50' 
                    : 'border-gray-300 hover:border-purple-300'
                }`}>
                  {selectedFile ? (
                    <div>
                      <FileText className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                      <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
                      <p className="text-xs text-gray-500">{(selectedFile.size / 1024).toFixed(1)} KB</p>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-900">{t('vault.clickToSelect')} {t('vault.document')}</p>
                    </>
                  )}
                </div>
                <input
                  id="doc-upload"
                  type="file"
                  accept=".pdf,.txt,.doc,.docx"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setSelectedFile(file);
                      setDocName(file.name);
                    }
                  }}
                />
              </label>

              {selectedFile && (
                <>
                  <div>
                    <Label>Name (Optional)</Label>
                    <Input
                      value={docName}
                      onChange={(e) => setDocName(e.target.value)}
                      placeholder={t('placeholders.documentName')}
                      className="mt-1"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => { setSelectedFile(null); setDocName(''); }} variant="outline" className="flex-1">
                      Clear
                    </Button>
                    <Button onClick={handleFileUpload} disabled={uploading} className="flex-1 bg-purple-600 hover:bg-purple-700">
                      {uploading ? 'Uploading...' : 'Upload'}
                    </Button>
                  </div>
                </>
              )}
            </Card>
          </div>

          <Card className="p-6 rounded-2xl">
            <h3 className="font-semibold text-gray-900 mb-4">{t('training.styleTraining')} ({savedTexts.length})</h3>
            {savedTexts.length > 0 ? (
              <div className="space-y-2">
                {savedTexts.map((item) => (
                  <div key={item.id} className="flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:border-purple-300 transition-all">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-medium text-gray-900">{item.title}</p>
                        <Badge className="text-xs bg-purple-100 text-purple-700">
                          {t('training.style')}
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-500">
                        {new Date(item.createdAt).toLocaleDateString()} • {((item.fileSize || 0) / 1024).toFixed(1)} KB
                      </p>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openViewDialog(item)} title="View content">
                        <Eye className="w-4 h-4 text-gray-400 hover:text-purple-600" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openRenameDialog(item)} title="Rename">
                        <Edit2 className="w-4 h-4 text-gray-400 hover:text-purple-600" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)} title="Delete">
                        <Trash2 className="w-4 h-4 text-gray-400 hover:text-red-600" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState
                icon={Pen}
                title={t('emptyStates.noStyleData')}
                description={t('emptyStates.noStyleDesc')}
                actionLabel={t('emptyStates.addTextSample')}
                onAction={() => document.querySelector('textarea[placeholder*="Paste"]')?.focus()}
                secondaryText="💡 Tip: Include emails, messages, or any content that reflects how you communicate"
              />
            )}
          </Card>
        </TabsContent>

        <TabsContent value="voice">
          <Card className="p-12 rounded-2xl text-center">
            <Clock className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Voice Style Coming Soon</h2>
            <p className="text-gray-600">
              Train your Twin on your speaking style, tone, and vocal patterns
            </p>
          </Card>
        </TabsContent>

        <TabsContent value="video">
          <Card className="p-12 rounded-2xl text-center">
            <Clock className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Video Style Coming Soon</h2>
            <p className="text-gray-600">
              Train your Twin on your gestures, expressions, and presentation style
            </p>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Style Sample</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>New Name</Label>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder={t('placeholders.enterNewName')}
                className="mt-1"
              />
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setRenameDialogOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleRename} className="flex-1 bg-purple-600 hover:bg-purple-700" disabled={!newName.trim()}>
                Rename
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Content Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pen className="w-5 h-5 text-purple-600" />
              {viewContent?.title || 'Style Sample'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {loadingContent ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
                <span className="ml-2 text-gray-600">Loading content...</span>
              </div>
            ) : viewContent ? (
              <>
                {/* File info */}
                <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                  <Badge className="bg-purple-100 text-purple-700">Style Sample</Badge>
                  <span>•</span>
                  <span>{viewContent.fileName}</span>
                  <span>•</span>
                  <span>{((viewContent.fileSize || 0) / 1024).toFixed(1)} KB</span>
                </div>
                
                {/* Preview type label */}
                {viewContent.previewType && (
                  <div className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getPreviewLabel(viewContent.previewType, viewContent.previewStatus).color}`}>
                    {getPreviewLabel(viewContent.previewType, viewContent.previewStatus).label}
                  </div>
                )}
                
                {/* Content display or fallback message */}
                {viewContent.previewStatus === 'failed' || viewContent.previewStatus === 'unsupported' || !viewContent.content ? (
                  <div className="bg-gray-50 rounded-lg p-6 text-center">
                    <AlertCircle className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600 font-medium">Preview not available for this file format.</p>
                    <p className="text-sm text-gray-500 mt-1">
                      The content has been processed for style extraction, but cannot be displayed here.
                    </p>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-lg p-4 max-h-[50vh] overflow-y-auto">
                    <pre className="whitespace-pre-wrap text-sm text-gray-800 font-mono">
                      {viewContent.content}
                    </pre>
                  </div>
                )}
                
                <p className="text-xs text-gray-500 italic">
                  This content is used only for style extraction. Your Twin learns your communication patterns, tone, and writing style from this sample.
                </p>
              </>
            ) : null}
            <div className="flex justify-end">
              <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
